'use client';

import React from 'react';
import { JsonLdScript } from './JsonLdScript';

/**
 * JSON-LD HowTo schema for process/procedure sections.
 * Enables rich results showing step-by-step instructions.
 *
 * Client component (same pattern as FAQPageSchema). The homepage passes it
 * `methodSteps` from `sections/homepage/method-steps.ts`, a plain module the
 * `ScientificMethod` cards also render from, so the schema and the visible
 * steps share one array. Keep step data in plain modules: an array exported
 * from a 'use client' module reaches a server component as a client
 * REFERENCE, which cannot be mapped over.
 */

export interface HowToStep {
  /** Title of the step (e.g., "We audit your visibility") */
  title: string;
  /** Description of what happens in this step */
  description: string;
}

interface HowToSchemaProps {
  /** Name of the procedure/how-to guide */
  name: string;
  /** Brief description of what the procedure accomplishes */
  description: string;
  /** Array of step objects with title and description */
  steps: HowToStep[];
  /** Full URL of the page (used for @id with #howto fragment) */
  pageUrl: string;
}

/**
 * Renders HowTo structured data as JSON-LD for process sections.
 *
 * @example
 * ```tsx
 * <HowToSchema
 *   name="How halcy.ai works"
 *   description="Get your healthcare practice found online"
 *   steps={[
 *     { title: 'We audit your visibility', description: 'See where you appear...' },
 *     { title: 'We build your presence', description: 'Structured content...' },
 *   ]}
 *   pageUrl="https://www.halcy.ai"
 * />
 * ```
 */
export function HowToSchema({
  name,
  description,
  steps,
  pageUrl,
}: HowToSchemaProps): React.ReactElement | null {
  if (steps.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    '@id': `${pageUrl}#howto`,
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.description,
    })),
  };

  return <JsonLdScript schema={schema} />;
}
